/**
 * Store container (section 11 "المخزن (المنتجات)", C33 — D07 §5; D-110 – D-116): binds `Store`, `StoreSalesAndReturns`
 * and the Store dialogs (packages/ui) to the read models `GET /store` (warehouses, products, stock per product ×
 * warehouse from the inventory ledger, the inventory cards) and `GET /store/operations` (purchases, sales, returns,
 * transfers and write-offs classified by operation type, D-115), and posts every movement through `/store/preview` →
 * PIN → `/store/commit` (D-310): the figures the PIN step shows are the server preview's `derived` strings (ADR-08 §2.1
 * item 2). A new product is created first (`POST /store/products`), a new supplier / customer is opened inside the
 * operation (`newAccounts`, D-018), an excess goes through the shared excess dialog (D-073, `SURPLUS_ACCOUNT_REQUIRED`).
 * Prints: the sales invoice on its own A4 layout (`SalesInvoiceA4`, C34, V-80) with its print audit (D-147). Labels are formatted here; the
 * only arithmetic is display sums / products over decimal strings (BigInt), never a posted figure.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PRINT_ROOT_CLASS, SalesInvoiceA4, StatementA4, Voucher80, printDocument, saleInvoice80, type SalesInvoiceA4Line, type SalesInvoiceA4Props, type Voucher80Props } from "@faylasuf/print";
import { CustomerVoucherModal } from "@faylasuf/ui";
import { canShareFiles, pendingTab, useDocuments, usePrintJob, useVoucherFormat } from "../app/documents.js";
import { printedNow } from "./customers/EntryVoucherContainer.js";
import {
  Store,
  StoreCancelDialog,
  StoreOperationDetails,
  StoreProductEditModal,
  StorePurchaseModal,
  StoreSalesAndReturns,
  StoreSellModal,
  StoreWriteoffModal,
  WarehouseManagerModal,
  formatWithCommas,
  type StoreDatePreset,
  type StoreOperationDetailsView,
  type StorePaymentKind,
  type StoreProductCard,
  type StorePurchaseValues,
  type StoreReturnValues,
  type StoreSellValues,
  type StoreStockOption,
  type StoreSubTab,
} from "@faylasuf/ui";
import type { AccountRow, PreferencesMap, StoreOperation, StorePreviewBody, StoreView } from "../api/types.js";
import { useApp, useSession } from "../app/context.js";
import { customerPinMessage, surplusPrimary, useExcessFlow, useNewCustomerName, type CustomerTarget, type ExcessResolution } from "../app/customer-picker.js";
import { engineErrors, errorMessage, isApiClientErrorLike, isSessionLost } from "./store/errors.js";
import { usePinGate } from "../app/pin-gate.js";
import { usePrintOffice } from "../app/printOffice.js";
import { keys } from "../app/query.js";
import { useToast } from "../app/toast.js";
import { useAccounts } from "./WalletsPage.js";
import { decAdd, decMulInt, decSub, isZero, toInt } from "./store/format.js";

const CASH_KINDS = new Set(["cash_vault", "cash_box", "digital_box", "custody"]);
const fmt = (s: string | null | undefined): string => formatWithCommas(s ?? "0");

export function StorePage() {
  const { t, locale, source, now } = useApp();
  const { session, hasPermission, isSuperManager, sessionLost } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const pin = usePinGate();
  const printOffice = usePrintOffice();
  const tz = session.office.timeZone;

  const storeQ = useQuery({ queryKey: keys.store, queryFn: () => source.store(), staleTime: 10_000 });
  const opsQ = useQuery({ queryKey: keys.storeOperations, queryFn: () => source.storeOperations({}), staleTime: 10_000 });
  const prefsQ = useQuery({ queryKey: keys.preferences, queryFn: () => source.preferences(), staleTime: 60_000 });
  const accountsQ = useAccounts(true);
  for (const q of [storeQ, opsQ]) if (q.error && isSessionLost(q.error)) sessionLost();
  const accent = (prefsQ.data as PreferencesMap | undefined)?.["ui.accent"];
  const themeColor = typeof accent === "string" ? accent : "#2563eb";

  const view: StoreView | undefined = storeQ.data;
  const ops: readonly StoreOperation[] = opsQ.data?.rows ?? [];
  const accounts: readonly AccountRow[] = accountsQ.data ?? [];

  const [subTab, setSubTab] = useState<StoreSubTab>("inventory");
  const [whFilter, setWhFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [purchase, setPurchase] = useState<{ productId: string; warehouseId: string } | null | false>(false);
  const [sell, setSell] = useState<{ multi: boolean; stockId: string | null } | null>(null);
  const [whOpen, setWhOpen] = useState(false);
  const [removeCard, setRemoveCard] = useState<StoreProductCard | null>(null);
  const [editCard, setEditCard] = useState<StoreProductCard | null>(null);
  const [cancelOp, setCancelOp] = useState<StoreOperation | null>(null);
  const [details, setDetails] = useState<StoreOperation | null>(null);
  const [transferSearch, setTransferSearch] = useState("");
  // sales filters
  const [search, setSearch] = useState("");
  const [salesWh, setSalesWh] = useState("all");
  const [datePreset, setDatePreset] = useState<StoreDatePreset>("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [payFilter, setPayFilter] = useState<"all" | StorePaymentKind>("all");
  const [prodFilter, setProdFilter] = useState("all");
  const [selectedSaleId, setSelectedSaleId] = useState("");
  // [C35] the sales invoice window (D-054: the dialog before printing; D-149 A4 / 80 mm per copy; D-146 PDF)
  const [printing, setPrinting] = useState<{ op: StoreOperation; buyer: { name: string | null; phone: string | null; address: string | null }; copy: boolean; words: string } | null>(null);
  const [saleFormat, setSaleFormat, saleCopyIssued] = useVoucherFormat("sales_invoice"); // [C36] T-18
  const saleJob = usePrintJob();
  const docs = useDocuments();
  const [saleBusy, setSaleBusy] = useState(false);
  // [C34] the purchase voucher (old "سند شراء بضاعة للمخزن", App.tsx:55761-55808) on the generic A4 template
  const [purchasePrint, setPurchasePrint] = useState<{ op: StoreOperation; newProductIds: ReadonlySet<string>; copy: boolean } | null>(null);

  const names = useNewCustomerName(accounts);
  const retry = useRef<((res: ExcessResolution) => Promise<void>) | null>(null);
  const excess = useExcessFlow({
    accounts,
    onReduce: () => toast(t("st.excess_reduce_hint"), "info"),
    onResolved: (res) => void retry.current?.(res),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: keys.store });
    void qc.invalidateQueries({ queryKey: keys.accounts });
    void qc.invalidateQueries({ queryKey: keys.journal });
  };

  // ---------------------------------------------------------------- names and options
  const whName = useMemo(() => new Map((view?.warehouses ?? []).map((w) => [w.id, w.name])), [view]);
  const mainWh = view?.warehouses.find((w) => w.isMain) ?? view?.warehouses[0];
  const activeWhs = (view?.warehouses ?? []).filter((w) => !w.archived);
  const warehouseOptions = activeWhs.map((w) => ({ id: w.id, label: `🏢 ${w.name}` }));
  const cashOptions = accounts.filter((a) => CASH_KINDS.has(a.kind) && a.currency === "IQD" && !a.archived).map((a) => ({ id: a.id, label: a.name ?? a.kind, subLabel: `${fmt(a.balance)} د.ع` }));
  const defaultCash = accounts.find((a) => a.kind === "cash_vault" && a.currency === "IQD")?.id ?? cashOptions[0]?.id ?? "";
  const partyOptions = (kind: "customer_debt" | "customer_trust") =>
    accounts.filter((a) => a.kind === kind && a.currency === "IQD" && !a.archived).map((a) => ({ id: a.id, label: a.name ?? "", subLabel: `${fmt(a.balance)} د.ع`, badge: kind === "customer_debt" ? t("st.pay_debt_badge") : t("st.pay_trust_badge") }));
  const debtOptions = partyOptions("customer_debt");
  const trustOptions = partyOptions("customer_trust");
  const supplierOptions = [...trustOptions, ...debtOptions];

  const activeProducts = (view?.products ?? []).filter((p) => !p.archived);
  const stockOptions: StoreStockOption[] = activeProducts.flatMap((p) =>
    p.stock.filter((s) => toInt(s.quantity) > 0).map((s) => ({ id: `${p.id}|${s.warehouseId}`, productId: p.id, warehouseId: s.warehouseId, label: `${p.name} — ${whName.get(s.warehouseId) ?? ""}`, subLabel: `${t("st.stock_short")} ${s.quantity} | ${t("st.price_short")} ${fmt(p.salePrice)}`, salePrice: p.salePrice })),
  );
  const manualOptions = activeProducts.flatMap((p) => activeWhs.map((w) => ({ id: `${p.id}|${w.id}`, label: `${p.name} — ${w.name}`, subLabel: `${t("st.stock_short")} ${p.stock.find((s) => s.warehouseId === w.id)?.quantity ?? "0"}` })));

  // ---------------------------------------------------------------- inventory cards (one per product × warehouse holding stock)
  const allCards: StoreProductCard[] = activeProducts.flatMap((p) => {
    const lines = p.stock.filter((s) => toInt(s.quantity) > 0);
    const make = (warehouseId: string | null, qty: string, avg: string | null): StoreProductCard => ({
      key: `${p.id}-${warehouseId ?? "none"}`,
      productId: p.id,
      warehouseId,
      name: p.name,
      stockLabel: qty,
      inStock: toInt(qty) > 0,
      warehouseName: warehouseId ? (whName.get(warehouseId) ?? "") : (mainWh?.name ?? t("st.main_store")),
      costLabel: fmt(avg ?? p.averageCost ?? "0"),
      saleLabel: fmt(p.salePrice),
    });
    return lines.length > 0 ? lines.map((s) => make(s.warehouseId, s.quantity, s.averageCost)) : [make(null, "0", null)];
  });
  const cardWh = (c: StoreProductCard) => c.warehouseId ?? mainWh?.id ?? "";
  const cards = whFilter === "all" ? allCards : allCards.filter((c) => cardWh(c) === whFilter);
  const kpis = useMemo(() => {
    if (!view) return { items: 0, unitsLabel: "0", costLabel: "0", saleLabel: "0", profitLabel: "0" };
    if (whFilter === "all") return { items: cards.length, unitsLabel: fmt(view.totals.units), costLabel: fmt(view.totals.valueAtCost), saleLabel: fmt(view.totals.expectedSaleValue), profitLabel: fmt(view.totals.potentialProfit) };
    const w = view.warehouses.find((x) => x.id === whFilter);
    let sale = "0";
    for (const p of activeProducts) {
      const s = p.stock.find((x) => x.warehouseId === whFilter);
      if (s) sale = decAdd(sale, decMulInt(p.salePrice, toInt(s.quantity)));
    }
    const cost = w?.valueAtCost ?? "0";
    return { items: cards.length, unitsLabel: fmt(w?.units ?? "0"), costLabel: fmt(cost), saleLabel: fmt(sale), profitLabel: fmt(decSub(sale, cost)) };
  }, [view, whFilter, cards.length]);

  // ---------------------------------------------------------------- lists by type (D-115)
  const sales = ops.filter((o) => o.type === "store_sale");
  const returns = ops.filter((o) => o.type === "store_return");
  const transfers = ops.filter((o) => o.type === "store_transfer");
  const saleById = new Map(sales.map((s) => [s.operationId, s]));
  const fmtDate = useMemo(() => new Intl.DateTimeFormat(locale === "ar" ? "ar-IQ" : "en-US", { timeZone: tz, year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }), [locale, tz]);
  const today = new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(now());
  const weekAgo = new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(now().getTime() - 6 * 86_400_000));
  const payOf = (o: StoreOperation): StorePaymentKind => (o.party ? (o.party.kind === "customer_trust" ? "trust" : "debt") : "cash");
  const buyerOf = (o: StoreOperation) => o.buyerName || o.party?.name || t("st.cash_customer");
  const inDate = (d: string) => (datePreset === "today" ? d === today : datePreset === "week" ? d >= weekAgo && d <= today : datePreset === "custom" ? (!from || d >= from) && (!to || d <= to) : true);
  const filteredSales = sales.filter((o) => {
    if (!inDate(o.officeDate)) return false;
    if (payFilter !== "all" && payOf(o) !== payFilter) return false;
    if (prodFilter !== "all" && !o.lines.some((l) => l.productId === prodFilter)) return false;
    if (salesWh !== "all" && !o.lines.some((l) => l.warehouseId === salesWh)) return false;
    const q = search.trim().toLowerCase();
    if (q && !`${o.invoiceNumber ?? ""} ${buyerOf(o)} ${o.lines.map((l) => l.productName ?? "").join(" ")}`.toLowerCase().includes(q)) return false;
    return true;
  });
  const live = filteredSales.filter((o) => o.status !== "cancelled");
  const liveIds = new Set(live.map((o) => o.operationId));
  const liveReturns = returns.filter((r) => r.status !== "cancelled" && r.saleOperationId && liveIds.has(r.saleOperationId));
  const stats = {
    revenueLabel: fmt(live.reduce((a, o) => decAdd(a, o.total), "0")),
    invoices: live.length,
    unitsLabel: fmt(String(live.reduce((a, o) => a + o.lines.reduce((b, l) => b + toInt(l.quantity), 0), 0))),
    // D-115: net sales profit subtracts the returns
    profitLabel: fmt([...live, ...liveReturns].reduce((a, o) => decAdd(a, o.profit), "0")),
  };
  const soldUnits = (o: StoreOperation) => o.lines.reduce((a, l) => a + toInt(l.quantity), 0);
  const returnedUnits = (o: StoreOperation) => o.lines.reduce((a, l) => a + toInt(l.returned), 0);
  const saleRows = filteredSales.map((o) => {
    const r = returnedUnits(o);
    const s = soldUnits(o);
    return {
      id: o.operationId,
      invoiceLabel: o.invoiceNumber ?? String(o.number),
      dateLabel: fmtDate.format(new Date(o.postedAt)),
      buyer: buyerOf(o),
      buyerPhone: null,
      items: o.lines.map((l) => ({ name: l.productName ?? "", qty: l.quantity })),
      payment: payOf(o),
      returnState: (r === 0 ? "none" : r >= s ? "full" : "partial") as "none" | "partial" | "full",
      returnedUnits: String(r),
      soldUnits: String(s),
      totalLabel: fmt(o.total),
      profitLabel: fmt(o.profit),
      operator: o.operator,
      cancelled: o.status === "cancelled",
    };
  });
  const returnRows = returns.map((o) => {
    const sale = o.saleOperationId ? saleById.get(o.saleOperationId) : undefined;
    return {
      id: o.operationId,
      invoiceLabel: sale ? (sale.invoiceNumber ?? String(sale.number)) : t("st.manual"),
      dateLabel: fmtDate.format(new Date(o.postedAt)),
      buyer: sale ? buyerOf(sale) : (o.party?.name ?? t("st.cash_customer")),
      details: o.lines.map((l) => `${l.productName ?? ""} ×${l.quantity}`).join("، "),
      boxName: o.cashAccount?.name ?? null,
      reason: o.reason ?? "-",
      refundLabel: fmt(o.total),
      operator: o.operator,
      cancelled: o.status === "cancelled",
    };
  });
  const selectedSale = saleById.get(selectedSaleId);
  const invoiceLines = (selectedSale?.lines ?? []).map((l) => ({
    key: l.accountId,
    productId: l.productId ?? "",
    warehouseId: l.warehouseId ?? "",
    name: `${l.productName ?? ""} — ${l.warehouseName ?? ""}`,
    sold: l.quantity,
    returned: l.returned,
    returnable: Math.max(0, toInt(l.quantity) - toInt(l.returned)),
    priceLabel: fmt(l.unitPrice),
    unitPrice: l.unitPrice ?? "0",
  }));
  const tq = transferSearch.trim().toLowerCase();
  const transferRows = transfers
    .map((o, i) => {
      const out = o.lines.find((l) => l.direction === "out");
      const inn = o.lines.find((l) => l.direction === "in");
      return {
        id: o.operationId,
        no: transfers.length - i,
        dateLabel: fmtDate.format(new Date(o.postedAt)),
        item: t("st.transfer_item", { name: out?.productName ?? "", qty: out?.quantity ?? "0", from: out?.warehouseName ?? "", to: inn?.warehouseName ?? "" }),
        note: o.note ?? "",
        operator: o.operator,
        cancelled: o.status === "cancelled",
      };
    })
    .filter((r) => !tq || `${r.item} ${r.note} ${r.operator}`.toLowerCase().includes(tq));

  // ---------------------------------------------------------------- posting through the gateway
  /** preview → PIN (message from the server's figures) → commit; SURPLUS_ACCOUNT_REQUIRED opens the excess dialog. */
  const post = async (body: StorePreviewBody, message: (d: Readonly<Record<string, string>>) => string, opts: { target?: CustomerTarget | null; kind?: "debt" | "trust"; onExcess?: (res: ExcessResolution) => Promise<void>; newAccount?: { name: string; kind: "debt" | "trust" } } = {}): Promise<{ operationId?: string; invoiceNumber: string | null } | undefined> => {
    setBusy(true);
    try {
      let pv;
      try {
        pv = await source.storePreview(body);
      } catch (e) {
        if (isSessionLost(e)) return void sessionLost();
        const sur = engineErrors(e).find((x) => x.code === "SURPLUS_ACCOUNT_REQUIRED");
        const primary = sur ? surplusPrimary(accounts, String(sur.params["account"] ?? ""), opts.target ?? null, opts.kind ?? "debt", "IQD") : undefined;
        if (sur && primary && opts.onExcess) {
          retry.current = opts.onExcess;
          excess.open({ account: primary, surplus: String(sur.params["surplus"] ?? "0") });
        } else toast(errorMessage(t, e), "error");
        return undefined;
      }
      const msg = customerPinMessage(t, message(pv.derived), { ...(opts.newAccount ? { newAccounts: [opts.newAccount] } : {}) });
      const res = await pin.withPin((proof) => source.storeCommit(pv.previewId, crypto.randomUUID(), proof), { message: msg, allowSupervisor: true });
      if (!res) return undefined;
      toast(res.status === "pending" ? t("st.toast_pending") : t("st.toast_posted"), res.status === "pending" ? "info" : "success");
      invalidate();
      return res.status === "posted" ? { operationId: res.operationId, invoiceNumber: res.invoiceNumber } : { invoiceNumber: null };
    } catch (e) {
      if (isSessionLost(e)) sessionLost();
      else toast(errorMessage(t, e), "error");
      return undefined;
    } finally {
      setBusy(false);
    }
  };

  // purchase: new products are created first (the catalogue), then one store_purchase
  const submitPurchase = async (v: StorePurchaseValues, res?: ExcessResolution, target0?: CustomerTarget | null): Promise<void> => {
    let target = target0 ?? null;
    if (v.funding.kind === "supplier" && !v.funding.partyAccountId && v.funding.newPartyName.trim() && !target) {
      target = await names.resolve(v.funding.newPartyName.trim(), "trust", "IQD", "p");
      if (!target) return;
    }
    const items = [];
    setBusy(true);
    try {
      for (const l of v.lines) {
        let productId = l.productId;
        if (!productId) {
          try {
            productId = (await source.createProduct({ name: l.name.trim(), salePrice: l.salePrice || "0" })).id;
          } catch (e) {
            if (isApiClientErrorLike(e) && e.code === "PRODUCT_NAME_TAKEN" && typeof e.params["productId"] === "string") productId = e.params["productId"];
            else throw e;
          }
          void qc.invalidateQueries({ queryKey: keys.store });
        }
        items.push({ productId, warehouseId: l.warehouseId, quantity: l.quantity, unitCost: l.unitCost, ...(l.salePrice ? { salePrice: l.salePrice } : {}) });
      }
    } catch (e) {
      toast(errorMessage(t, e), "error");
      return;
    } finally {
      setBusy(false);
    }
    const partyId = v.funding.partyAccountId || target?.accountId || "";
    const funding =
      v.funding.kind === "cash"
        ? { kind: "cash" as const, cashAccountId: v.funding.cashAccountId }
        : { kind: v.funding.kind, party: { accountId: partyId, ...(res ? { surplus: res.surplus } : {}) }, ...(v.funding.cash ? { cash: v.funding.cash, cashAccountId: v.funding.cashAccountId } : {}) };
    const newAccounts = [...(target && !v.funding.partyAccountId ? target.newAccounts : []), ...(res?.newAccounts ?? [])];
    const body: StorePreviewBody = { type: "store_purchase", input: { items, funding, ...(v.note ? { note: v.note } : {}) }, ...(newAccounts.length > 0 ? { newAccounts: [...newAccounts] } : {}) };
    const done = await post(body, (d) => t("st.pin_purchase", { total: fmt(d["total"]), cash: fmt(d["cash"]), onAccount: fmt(d["on_account"]) }), {
      target,
      kind: v.funding.kind === "from_trust" ? "trust" : "debt",
      onExcess: (r) => submitPurchase(v, r, target),
      ...(target?.newAccount && !v.funding.partyAccountId ? { newAccount: target.newAccount } : {}),
    });
    if (!done) return;
    setPurchase(false);
    if (v.print && done.operationId) {
      const newProductIds = new Set(items.filter((_, i) => !v.lines[i]!.productId).map((it) => it.productId));
      try {
        const fresh = await qc.fetchQuery({ queryKey: keys.storeOperations, queryFn: () => source.storeOperations({}) });
        const op = fresh.rows.find((o) => o.operationId === done.operationId);
        if (op) {
          const audit = await source.storePrinted(op.operationId, "print");
          setPurchasePrint({ op, newProductIds, copy: audit.copy });
        }
      } catch (e) {
        toast(errorMessage(t, e), "error");
      }
    }
  };

  const submitSale = async (v: StoreSellValues, res?: ExcessResolution, target0?: CustomerTarget | null): Promise<void> => {
    let target = target0 ?? null;
    if (v.counterParty === "customer" && !v.customerAccountId && v.newCustomerName.trim() && !target) {
      target = await names.resolve(v.newCustomerName.trim(), "debt", "IQD", "c");
      if (!target) return;
    }
    const partyId = v.customerAccountId || target?.accountId || "";
    const payment =
      v.counterParty === "box"
        ? { cashAccountId: v.cashAccountId }
        : { party: { accountId: partyId, ...(res ? { surplus: res.surplus } : {}) }, ...(v.receivedCash ? { cash: v.receivedCash } : {}), ...(v.remainingOnAccount ? { onAccount: v.remainingOnAccount } : {}), ...(v.receivedCash || v.remainingOnAccount ? { cashAccountId: v.cashAccountId } : {}) };
    const newAccounts = [...(target && !v.customerAccountId ? target.newAccounts : []), ...(res?.newAccounts ?? [])];
    const buyer = v.buyerName || v.buyerPhone || v.buyerAddress ? { buyer: { ...(v.buyerName ? { name: v.buyerName } : {}), ...(v.buyerPhone ? { phone: v.buyerPhone } : {}), ...(v.buyerAddress ? { address: v.buyerAddress } : {}) } } : {};
    const body: StorePreviewBody = { type: "store_sale", input: { items: v.lines.map((l) => ({ ...l })), ...(v.discount ? { discount: v.discount } : {}), payment, ...(v.note ? { note: v.note } : {}) }, ...buyer, ...(newAccounts.length > 0 ? { newAccounts: [...newAccounts] } : {}) };
    const done = await post(body, (d) => t("st.pin_sale", { total: fmt(d["total"]), discount: fmt(d["discount"]), profit: fmt(d["profit"]), cash: fmt(d["cash"]), onAccount: fmt(d["on_account"]) }), {
      target,
      kind: v.allocation,
      onExcess: (r) => submitSale(v, r, target),
      ...(target?.newAccount && !v.customerAccountId ? { newAccount: target.newAccount } : {}),
    });
    if (!done) return;
    setSell(null);
    if (v.print && done.operationId) {
      const fresh = await qc.fetchQuery({ queryKey: keys.storeOperations, queryFn: () => source.storeOperations({}) });
      const op = fresh.rows.find((o) => o.operationId === done.operationId);
      if (op) void printSale(op);
    }
  };

  const submitReturn = async (v: StoreReturnValues, res?: ExcessResolution): Promise<void> => {
    const refund =
      v.method === "cash" ? { cashAccountId: v.cashAccountId } : { party: { accountId: v.customerAccountId, ...(res ? { surplus: res.surplus } : {}) } };
    const body: StorePreviewBody = {
      type: "store_return",
      input: { ...(v.saleId ? { saleOperationId: v.saleId } : {}), items: v.lines.map((l) => ({ ...l })), refund, ...(v.reason ? { reason: v.reason } : {}), ...(v.note ? { note: v.note } : {}) },
      ...(res && res.newAccounts.length > 0 ? { newAccounts: [...res.newAccounts] } : {}),
    };
    const done = await post(body, (d) => t("st.pin_return", { refund: fmt(d["refund"]), cost: fmt(d["cost"]) }), { kind: v.method === "customer_trust" ? "trust" : "debt", onExcess: (r) => submitReturn(v, r) });
    if (done) setSelectedSaleId("");
  };

  const simple = async (fn: () => Promise<unknown>, okKey: string): Promise<boolean> => {
    setBusy(true);
    try {
      await fn();
      toast(t(okKey), "success");
      invalidate();
      return true;
    } catch (e) {
      if (isSessionLost(e)) sessionLost();
      else toast(errorMessage(t, e), "error");
      return false;
    } finally {
      setBusy(false);
    }
  };
  const withPinSimple = async (fn: (proof: { pin?: string; supervisor?: { username: string; pin: string } }) => Promise<unknown>, okKey: string, message: string): Promise<boolean> => {
    setBusy(true);
    try {
      const r = await pin.withPin(fn, { message, allowSupervisor: true });
      if (r === undefined) return false;
      toast(t(okKey), "success");
      invalidate();
      return true;
    } catch (e) {
      if (isSessionLost(e)) sessionLost();
      else toast(errorMessage(t, e), "error");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const confirmCancel = async (reason: string) => {
    const op = cancelOp;
    if (!op) return;
    setBusy(true);
    try {
      const r = await pin.withPin((proof) => source.storeCancel(op.operationId, reason, crypto.randomUUID(), proof.pin ?? ""), { message: t("st.pin_cancel", { total: fmt(op.total) }) });
      if (r) {
        toast(t("st.toast_cancelled"), "success");
        setCancelOp(null);
        invalidate();
      }
    } catch (e) {
      if (isSessionLost(e)) sessionLost();
      else toast(errorMessage(t, e), "error");
    } finally {
      setBusy(false);
    }
  };

  // ---------------------------------------------------------------- print (D-147: the audit call first, then the A4 page)
  const printSale = async (op: StoreOperation) => {
    try {
      const buyer = await source.storeBuyer(op.operationId);
      const [words] = await docs.words([{ lines: [{ amount: op.total, currency: "IQD" }] }]);
      setPrinting({ op, buyer, copy: false, words: words ?? "" });
    } catch (e) {
      toast(errorMessage(t, e), "error");
    }
  };
  const saleIssue = async (channel: "print" | "pdf" | "whatsapp"): Promise<boolean | null> => {
    if (!printing) return null;
    setSaleBusy(true);
    try {
      return (await source.storePrinted(printing.op.operationId, channel)).copy;
    } catch (e) {
      if (isSessionLost(e)) sessionLost();
      else toast(errorMessage(t, e), "error");
      return null;
    } finally {
      setSaleBusy(false);
    }
  };
  useEffect(() => {
    if (!purchasePrint) return;
    const done = () => setPurchasePrint(null);
    window.addEventListener("afterprint", done, { once: true });
    const h = setTimeout(() => printDocument("professional-print-template"), 80);
    return () => {
      clearTimeout(h);
      window.removeEventListener("afterprint", done);
    };
  }, [purchasePrint]);
  // [C34] V-80: the dedicated sales-invoice layout (old ProfessionalPrint sales branch, App.tsx:12475-12795)
  const printCash = printing ? !printing.op.party : true;
  const printLines: SalesInvoiceA4Line[] = (printing?.op.lines ?? []).map((l) => ({ name: l.productName ?? "", quantity: l.quantity, priceLabel: `${fmt(l.unitPrice)} د.ع`, totalLabel: `${fmt(l.total)} د.ع` }));
  const printSummary = printing
    ? [
        ...(isZero(printing.op.discount) ? [] : [{ label: t("st.print_gross"), value: `${fmt(printing.op.gross)} د.ع` }, { label: t("st.discount"), value: `${fmt(printing.op.discount)} د.ع` }]),
        ...(printing.op.party ? [{ label: t("print.sale.account"), value: printing.op.party.name ?? "" }] : []),
        { label: t("st.col_operator"), value: printing.op.operator },
      ]
    : [];

  // ---------------------------------------------------------------- details
  const detailsView: StoreOperationDetailsView | null = details
    ? {
        title: `${t(`jr.type.${details.type}`)} #${details.invoiceNumber ?? details.number}`,
        fields: [
          { label: t("st.col_datetime"), value: fmtDate.format(new Date(details.postedAt)) },
          { label: t("st.col_operator"), value: details.operator },
          { label: t("st.print_total"), value: `${fmt(details.total)} د.ع` },
          ...(details.type === "store_sale" || details.type === "store_return" ? [{ label: t("st.col_profit"), value: `${fmt(details.profit)} د.ع` }] : []),
          ...(details.party ? [{ label: t("st.refund_customer"), value: details.party.name ?? "" }] : []),
          ...(details.cashAccount ? [{ label: t("st.funding_box"), value: details.cashAccount.name ?? "" }] : []),
          ...(details.reason ? [{ label: t("st.col_reason"), value: details.reason }] : []),
          ...(details.note ? [{ label: t("st.note"), value: details.note }] : []),
          ...(details.cancel ? [{ label: t("st.badge_cancelled"), value: details.cancel.reason ?? "" }] : []),
        ],
        lines: details.lines.map((l) => ({ name: l.productName ?? "", warehouse: l.warehouseName ?? "", qty: `${l.direction === "out" ? "-" : "+"}${l.quantity}`, price: fmt(l.unitPrice ?? l.unitRefund ?? l.unitCost), total: fmt(l.total ?? l.cost) })),
      }
    : null;

  if (!view) return <div className="p-12 text-center text-sm font-bold text-gray-400" data-testid="store-loading">{storeQ.error ? errorMessage(t, storeQ.error) : t("st.loading")}</div>;

  const removeProduct = removeCard ? view.products.find((p) => p.id === removeCard.productId) : undefined;
  const editProduct = editCard ? view.products.find((p) => p.id === editCard.productId) : undefined;
  const canCancel = hasPermission("delete_movements");

  return (
    <>
      <Store
        themeColor={themeColor}
        badgeCount={view.totals.items}
        subTab={subTab}
        onSubTab={setSubTab}
        counts={{ inventory: cards.length, sales: sales.length, returns: returns.length, transfers: transfers.length }}
        canManageWarehouses={view.canManageWarehouses}
        canOperate={view.canOperate}
        onManageWarehouses={() => setWhOpen(true)}
        onAddProduct={() => setPurchase(null)}
        onSellHeader={() => setSell({ multi: false, stockId: stockOptions[0]?.id ?? null })}
        onNewSellList={() => setSell({ multi: true, stockId: null })}
        // old condition (App.tsx:40973): admin / Super Manager, no warehouse limit, or more than one allowed warehouse
        showAllPill={isSuperManager || hasPermission("manage_warehouses") || activeWhs.length > 1}
        allCount={allCards.length}
        warehousePills={activeWhs.map((w) => ({ id: w.id, name: w.name, count: allCards.filter((c) => cardWh(c) === w.id).length }))}
        selectedWarehouseId={isSuperManager || hasPermission("manage_warehouses") || activeWhs.length > 1 ? whFilter : (mainWh?.id ?? "all")}
        onSelectWarehouse={setWhFilter}
        kpis={kpis}
        cards={cards}
        onHistory={(c) => {
          setProdFilter(c.productId);
          setSubTab("sales_invoices");
        }}
        onRemove={(c) => setRemoveCard(c)}
        onEdit={(c) => setEditCard(c)}
        onBuy={(c) => setPurchase({ productId: c.productId, warehouseId: cardWh(c) })}
        onSell={(c) => setSell({ multi: false, stockId: c.warehouseId ? `${c.productId}|${c.warehouseId}` : null })}
        transferSearch={transferSearch}
        onTransferSearch={setTransferSearch}
        transfersTotal={transfers.length}
        activeWarehouses={activeWhs.length}
        transferRows={transferRows}
        onTransferDetails={(id) => setDetails(transfers.find((o) => o.operationId === id) ?? null)}
        salesAndReturns={
          <StoreSalesAndReturns
            activeSubTab={subTab === "sales_returns" ? "sales_returns" : "sales_invoices"}
            onSubTab={setSubTab}
            stats={stats}
            search={search}
            onSearch={setSearch}
            warehouseOptions={[{ id: "all", label: t("st.filter_wh_all") }, ...warehouseOptions.map((w) => ({ ...w, badge: t("st.warehouse") }))]}
            warehouseFilter={salesWh}
            onWarehouseFilter={setSalesWh}
            datePreset={datePreset}
            onDatePreset={setDatePreset}
            from={from}
            to={to}
            onFrom={setFrom}
            onTo={setTo}
            paymentFilter={payFilter}
            onPaymentFilter={setPayFilter}
            productOptions={activeProducts.map((p) => ({ id: p.id, label: p.name, subLabel: `${t("st.stock_short")} ${p.quantity}` }))}
            productFilter={prodFilter}
            onProductFilter={setProdFilter}
            sales={saleRows}
            canCancel={canCancel}
            onView={(id) => setDetails(saleById.get(id) ?? null)}
            onPrint={(id) => {
              const op = saleById.get(id);
              if (op) void printSale(op);
            }}
            onCancel={(id) => setCancelOp(saleById.get(id) ?? null)}
            onNewSale={() => setSell({ multi: true, stockId: null })}
            saleOptions={sales.filter((o) => o.status !== "cancelled").map((o) => ({ id: o.operationId, label: `#${o.invoiceNumber ?? o.number} — ${buyerOf(o)}`, subLabel: `${fmt(o.total)} د.ع · ${o.officeDate}` }))}
            selectedSaleId={selectedSaleId}
            onSelectSale={setSelectedSaleId}
            invoiceLines={invoiceLines}
            invoiceFullyReturned={!!selectedSale && invoiceLines.every((l) => l.returnable === 0)}
            canManualReturn={view.canManualReturn}
            manualProductOptions={manualOptions}
            cashAccountOptions={cashOptions}
            defaultCashAccountId={defaultCash}
            debtAccountOptions={debtOptions}
            trustAccountOptions={trustOptions}
            busy={busy}
            onSubmitReturn={(v) => void submitReturn(v)}
            returns={returnRows}
            onViewReturn={(id) => setDetails(returns.find((o) => o.operationId === id) ?? null)}
            onCancelReturn={(id) => setCancelOp(returns.find((o) => o.operationId === id) ?? null)}
          />
        }
      />
      <StorePurchaseModal
        open={purchase !== false}
        themeColor={themeColor}
        products={activeProducts.map((p) => ({ id: p.id, name: p.name, salePrice: p.salePrice, costLabel: fmt(p.averageCost ?? "0"), stockLabel: p.quantity }))}
        warehouseOptions={warehouseOptions}
        defaultWarehouseId={mainWh?.id ?? ""}
        initial={purchase || null}
        cashAccountOptions={cashOptions}
        defaultCashAccountId={defaultCash}
        supplierOptions={supplierOptions}
        trustOptions={trustOptions}
        busy={busy}
        onSubmit={(v) => void submitPurchase(v)}
        onClose={() => setPurchase(false)}
      />
      <StoreSellModal
        open={sell !== null}
        multi={sell?.multi ?? false}
        themeColor={themeColor}
        stockOptions={stockOptions}
        initialStockId={sell?.stockId ?? null}
        cashAccountOptions={cashOptions}
        defaultCashAccountId={defaultCash}
        debtOptions={debtOptions}
        trustOptions={trustOptions}
        busy={busy}
        onSubmit={(v) => void submitSale(v)}
        onClose={() => setSell(null)}
      />
      <WarehouseManagerModal
        open={whOpen}
        warehouses={view.warehouses.map((w) => ({ id: w.id, name: w.name, location: w.location, description: w.description, isMain: w.isMain, archived: w.archived, products: w.products, unitsLabel: fmt(w.units) }))}
        stockOptions={stockOptions}
        busy={busy}
        onCreate={(v) => void simple(() => source.createWarehouse({ name: v.name, ...(v.location ? { location: v.location } : {}), ...(v.description ? { description: v.description } : {}) }), "st.toast_saved")}
        onUpdate={(v) => void simple(() => source.updateWarehouse({ id: v.id, name: v.name, location: v.location || null, description: v.description || null }), "st.toast_saved")}
        onArchive={(id, archive) => void withPinSimple((proof) => source.archiveWarehouse(id, archive, proof), "st.toast_saved", t(archive ? "st.pin_archive_wh" : "st.pin_restore_wh", { name: whName.get(id) ?? "" }))}
        onTransfer={(v) =>
          void post({ type: "store_transfer", input: { productId: v.productId, fromWarehouseId: v.fromWarehouseId, toWarehouseId: v.toWarehouseId, quantity: v.quantity, ...(v.note ? { note: v.note } : {}) } }, (d) => t("st.pin_transfer", { qty: d["quantity"] ?? v.quantity, cost: fmt(d["cost"]) }))
        }
        onClose={() => setWhOpen(false)}
      />
      <StoreWriteoffModal
        open={removeCard !== null}
        productName={removeCard?.name ?? ""}
        warehouseName={removeCard?.warehouseName ?? ""}
        stockLabel={removeCard?.stockLabel ?? "0"}
        hasStock={!!removeProduct && toInt(removeProduct.quantity) > 0}
        canWriteoff={view.canWriteoff && !!removeCard?.warehouseId && (removeCard?.inStock ?? false)}
        canArchive={view.canOperate}
        busy={busy}
        onWriteoff={(v) =>
          void post({ type: "store_writeoff", input: { productId: removeCard!.productId, warehouseId: removeCard!.warehouseId!, quantity: v.quantity, reason: v.reason } }, (d) => t("st.pin_writeoff", { qty: d["quantity"] ?? v.quantity, cost: fmt(d["cost"]) })).then((r) => {
            if (r) setRemoveCard(null);
          })
        }
        onArchive={() =>
          void withPinSimple((proof) => source.archiveProduct(removeCard!.productId, true, proof), "st.toast_archived", t("st.pin_archive_product", { name: removeCard?.name ?? "" })).then((ok) => {
            if (ok) setRemoveCard(null);
          })
        }
        onClose={() => setRemoveCard(null)}
      />
      <StoreProductEditModal
        open={editCard !== null}
        name={editProduct?.name ?? ""}
        salePrice={editProduct?.salePrice ?? ""}
        busy={busy}
        onSave={(v) =>
          void simple(() => source.updateProduct({ id: editCard!.productId, name: v.name, salePrice: v.salePrice }), "st.toast_saved").then((ok) => {
            if (ok) setEditCard(null);
          })
        }
        onClose={() => setEditCard(null)}
      />
      <StoreCancelDialog open={cancelOp !== null} title={cancelOp?.type === "store_return" ? t("st.cancel_return") : t("st.cancel_invoice")} busy={busy} onConfirm={(r) => void confirmCancel(r)} onClose={() => setCancelOp(null)} />
      <StoreOperationDetails
        view={detailsView}
        onClose={() => setDetails(null)}
        {...(details?.type === "store_sale" ? { onPrint: () => void printSale(details) } : {})}
      />
      {excess.dialog}
      {names.dialog}
      <div className="relative z-[130]">{pin.dialog}</div>
      {purchasePrint &&
        createPortal(
          <div className={PRINT_ROOT_CLASS} data-testid="print-root">
            <StatementA4
              locale={locale}
              title={`${t("print.purchase.title")}${purchasePrint.copy ? ` — ${t("st.print_copy")}` : ""}`}
              dateLabel={fmtDate.format(new Date(purchasePrint.op.postedAt))}
              docNumber={String(purchasePrint.op.number)}
              office={printOffice}
              columns={[
                { header: t("print.purchase.col_product"), key: "name" },
                { header: t("print.purchase.col_warehouse"), key: "storeName" },
                { header: t("print.purchase.col_status"), key: "status" },
                { header: t("print.purchase.col_quantity"), key: "quantity", numeric: true },
                { header: t("print.purchase.col_cost"), key: "costPrice", numeric: true },
                { header: t("print.purchase.col_total"), key: "total", numeric: true },
              ]}
              rows={purchasePrint.op.lines
                .filter((l) => l.direction === "in")
                .map((l) => ({
                  name: l.productName ?? "",
                  storeName: l.warehouseName ?? "",
                  status: l.productId && purchasePrint.newProductIds.has(l.productId) ? t("print.purchase.new_product") : t("print.purchase.stock_update"),
                  quantity: l.quantity,
                  costPrice: `${fmt(l.unitCost ?? "0")} د.ع`,
                  total: `${fmt(l.cost)} د.ع`,
                }))}
              summary={[
                { label: t("print.purchase.total_cost"), value: `${fmt(purchasePrint.op.total)} د.ع` },
                {
                  label: t("print.purchase.payment"),
                  value: purchasePrint.op.funding === "supplier" ? t("print.purchase.pay_supplier") : purchasePrint.op.funding === "from_trust" ? t("print.purchase.pay_trust") : t("print.purchase.pay_cash"),
                },
                ...(purchasePrint.op.party ? [{ label: t("print.purchase.party"), value: purchasePrint.op.party.name ?? "" }] : []),
                ...(isZero(purchasePrint.op.onAccount) ? [] : [{ label: t("st.remaining_on_account"), value: `${fmt(purchasePrint.op.onAccount)} د.ع` }]),
                { label: t("print.purchase.box"), value: purchasePrint.op.cashAccount?.name ?? t("print.purchase.no_cash") },
                { label: t("print.purchase.operator"), value: purchasePrint.op.operator },
              ]}
            />
          </div>,
          document.body,
        )}
      {printing && (() => {
        const sale = (copy: boolean): SalesInvoiceA4Props => ({
          locale,
          title: `${t(printCash ? "print.sale.title_cash" : "print.sale.title_credit", { n: printing.op.invoiceNumber ?? String(printing.op.number) })}${copy ? ` — ${t("st.print_copy")}` : ""}`,
          dateLabel: fmtDate.format(new Date(printing.op.postedAt)),
          docNumber: printing.op.invoiceNumber ?? String(printing.op.number),
          office: printOffice,
          cashSale: printCash,
          buyerName: printing.buyer.name || buyerOf(printing.op),
          buyerPhone: printing.buyer.phone,
          buyerAddress: printing.buyer.address,
          paymentMethodLabel: printCash ? t("print.sale.pay_cash") : payOf(printing.op) === "trust" ? t("print.sale.pay_trust") : t("print.sale.pay_debt"),
          grandTotalLabel: `${fmt(printing.op.total)} د.ع`,
          receivedLabel: printCash ? null : `${fmt(printing.op.cash)} د.ع`,
          remainingLabel: printCash ? null : `${fmt(printing.op.onAccount)} د.ع`,
          lines: printLines,
          summary: printSummary,
          wordsLabel: printing.words || null, // [C35] D-148 (:1082-1087)
        });
        const docOf = (copy: boolean) =>
          saleFormat === "80mm"
            ? { template: "voucher_80mm" as const, props: saleInvoice80(sale(copy), printing.op.operator, printedNow(session.office.timeZone, locale)) }
            : { template: "sales_invoice_a4" as const, props: sale(copy) };
        const render = (d: ReturnType<typeof docOf>, id?: string) =>
          d.template === "voucher_80mm" ? <Voucher80 {...(d.props as Voucher80Props)} {...(id ? { id } : {})} /> : <SalesInvoiceA4 {...(d.props as SalesInvoiceA4Props)} />;
        const req = (copy: boolean) => {
          const d = docOf(copy);
          return { template: d.template, props: d.props as unknown as Record<string, unknown>, fileName: `invoice-${printing.op.invoiceNumber ?? printing.op.number}`, docType: "sales_invoice", operationId: printing.op.operationId, ...(printing.op.invoiceNumber ? { number: String(printing.op.invoiceNumber) } : {}) };
        };
        const preview = docOf(printing.copy);
        return (
          <CustomerVoucherModal
            title={t("print.sale.modal_title")}
            busy={saleBusy || saleJob.busy}
            format={saleFormat}
            onFormatChange={setSaleFormat}
            onClose={() => setPrinting(null)}
            onPrint={() =>
              void saleIssue("print").then((copy) => {
                if (copy === null) return;
                const d = docOf(copy);
                saleJob.start(d.template === "voucher_80mm" ? render(d, "sale-voucher-80mm-print") : render(d), d.template === "voucher_80mm" ? "sale-voucher-80mm-print" : "professional-print-template", saleFormat);
                saleCopyIssued();
              })
            }
            onExportPdf={() => void saleIssue("pdf").then((copy) => (copy === null ? undefined : docs.downloadPdf(req(copy)).finally(saleCopyIssued)))}
            onSharePdf={() => void saleIssue("pdf").then((copy) => (copy === null ? undefined : docs.sharePdf(req(copy)).finally(saleCopyIssued)))}
            onWhatsApp={() => {
              const tab = canShareFiles() ? null : pendingTab(); // [C35 review 9] opened inside the click
              void saleIssue("whatsapp").then((copy): Promise<unknown> | undefined =>
                copy === null ? void tab?.cancel() : docs.sharePdf(req(copy), { phone: printing.buyer.phone, text: `${printOffice.name}\n${sale(copy).title}\n${t("print.sale.grand_total")} ${fmt(printing.op.total)} د.ع` }, tab).finally(saleCopyIssued),
              );
            }}
          >
            {preview.template === "voucher_80mm" ? <div className="py-4 flex justify-center">{render(preview)}</div> : <div className="overflow-x-auto">{render(preview)}</div>}
          </CustomerVoucherModal>
        );
      })()}
      {saleJob.portal}
    </>
  );
}
